import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Delete } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isKiosk } from '@shared/lib/constants';

const SERVICE_PASSWORD = 'service';

// In-modal touch keyboard. The kiosk runs a bare-X session (no GNOME)
// so there's no system on-screen keyboard, and even with GNOME the
// system OSK is unreliable — the password modal would dismiss while
// the user was tapping. Rendering the keyboard inside the modal means
// every keypress is a React click within the same event tree, so no
// focus or backdrop-click weirdness can dismiss the modal mid-entry.
const KEYBOARD_ROWS = [
  ['q','w','e','r','t','y','u','i','o','p'],
  ['a','s','d','f','g','h','j','k','l'],
  ['z','x','c','v','b','n','m'],
];

export function ServicePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [value, setValue] = useState('');
  const [shaking, setShaking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setShaking(false);
      // Focus a hidden text input so a real keyboard still works for
      // engineers SSH-tunnelling or VNC'd in.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  function attempt(submitted: string) {
    if (submitted === SERVICE_PASSWORD) {
      window.location.assign('/');
      return;
    }
    setShaking(true);
    setValue('');
    requestAnimationFrame(() => inputRef.current?.focus());
    setTimeout(() => setShaking(false), 400);
  }

  function tap(ch: string) {
    setValue((v) => {
      const next = v + ch;
      // Auto-submit when the entered string matches the password length;
      // shorter strings stay open. The user can also tap Enter.
      if (next.length >= SERVICE_PASSWORD.length) {
        // Defer attempt so React commits the value first (so a wrong
        // password renders briefly before the shake clears it).
        setTimeout(() => attempt(next), 0);
      }
      return next;
    });
  }

  function backspace() {
    setValue((v) => v.slice(0, -1));
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={cn(
          'w-full max-w-2xl rounded-3xl border border-border bg-surface p-6 shadow-2xl',
          shaking && 'animate-[shake_0.4s_ease-in-out]',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-1">Service menu</h2>
        <p className="text-sm text-muted mb-4">Enter the service password to continue.</p>

        {/* Visible bullet field. The real <input> below is hidden but
            still accepts hardware-keyboard input (so engineers VNC'd or
            with a USB keyboard plugged in can type normally). */}
        <div
          className="w-full mb-5 flex items-center gap-2 rounded-2xl border-2 border-border bg-background px-5 py-4 text-foreground select-none"
          onClick={() => inputRef.current?.focus()}
        >
          <div className="flex-1 flex items-center gap-2 text-2xl tracking-[0.4em] font-mono min-h-[1.5em]">
            {value
              ? Array.from(value).map((_, i) => <span key={i}>•</span>)
              : <span className="text-muted/50 text-sm tracking-normal font-sans">Tap a key…</span>}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); backspace(); }}
            disabled={value.length === 0}
            className="rounded-full px-3 py-2 text-sm font-semibold border border-border bg-surface-muted hover:bg-surface-strong text-muted hover:text-foreground transition disabled:opacity-30"
            aria-label="Delete last character"
          >
            <Delete className="h-5 w-5" />
          </button>
        </div>

        {/* Hidden real input — receives hardware-keyboard events, autofocused. */}
        <input
          ref={inputRef}
          type="text"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') attempt(value); }}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          aria-hidden="true"
        />

        {/* Touch keyboard. Three rows of single-character keys, plus a
            bottom row with Cancel / space (no-op for passwords) / Enter. */}
        <div className="flex flex-col gap-2">
          {KEYBOARD_ROWS.map((row, ri) => (
            <div key={ri} className="flex justify-center gap-2">
              {row.map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => tap(ch)}
                  className="flex-1 max-w-[3.5rem] rounded-xl border-2 border-border bg-surface-muted hover:bg-surface-strong active:scale-95 px-2 py-3 text-base font-semibold text-foreground transition"
                >
                  {ch}
                </button>
              ))}
            </div>
          ))}
          <div className="flex justify-center gap-2 mt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 max-w-[8rem] rounded-xl border-2 border-border bg-surface-muted hover:bg-surface-strong active:scale-95 px-4 py-3 text-base font-semibold text-muted hover:text-foreground transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => attempt(value)}
              disabled={value.length === 0}
              className="flex-1 max-w-[10rem] rounded-xl bg-accent hover:opacity-90 active:scale-95 px-4 py-3 text-base font-semibold text-white transition disabled:opacity-40"
            >
              Enter
            </button>
          </div>
        </div>

        {!isKiosk && (
          <p className="mt-4 text-center text-xs text-muted/70">
            Tip: a hardware keyboard works too — type the password and press Enter.
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
